import { type StoreGroupKind, storeGroupPath } from '@arabat/contracts';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router';
import { storePath } from '../address.js';
import { DesignedPage } from '../design/sections.js';
import {
  RouteGroupContext,
  SALE_GROUP,
  findGroup,
  groupPath,
  groupsOf,
  useSlug,
  useStorefront,
} from '../shared.js';

/*
 * Readable store addresses (owner decision 2026-11-03, migration 0107): a category, collection
 * or brand has its own page, `/categories/{handle}`, `/collections/{handle}`, `/brands/{handle}`,
 * drawn as the products page listing that group. An id (old links) moves to the handle; the
 * server answers the same with a 301 when the address is opened directly.
 */

/** A category, collection or brand page. */
export function GroupPage({ kind }: { kind: StoreGroupKind }) {
  const { handle = '' } = useParams();
  const slug = useSlug();
  const location = useLocation();
  const { data: store } = useStorefront();
  const list = store ? groupsOf(store, kind) : [];
  const group = list.find((g) => g.handle === handle) ?? list.find((g) => g.id === handle);
  if (group?.handle && group.handle !== handle)
    return (
      <Navigate
        replace
        to={`${storePath(slug, storeGroupPath(kind, group))}${location.search}${location.hash}`}
      />
    );
  return (
    <RouteGroupContext.Provider value={group?.id ?? handle}>
      <DesignedPage page="products" />
    </RouteGroupContext.Provider>
  );
}

/**
 * The products and categories pages. The old `?category={id}` address of a category, collection
 * or brand moves to the group's own page; `?category=sale` stays the sale page.
 */
export function ListPage({ page }: { page: 'products' | 'categories' }) {
  const [params] = useSearchParams();
  const slug = useSlug();
  const { data: store } = useStorefront();
  const id = params.get('category');
  const group = id && id !== SALE_GROUP && store ? findGroup(store, id) : undefined;
  if (group && store) {
    const rest = new URLSearchParams(params);
    rest.delete('category');
    const search = rest.toString();
    return (
      <Navigate
        replace
        to={`${storePath(slug, groupPath(store, group.id))}${search ? `?${search}` : ''}`}
      />
    );
  }
  return <DesignedPage page={page} />;
}
